import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  History as HistoryIcon,
  Bot,
  Target,
  Shield,
  RefreshCw,
  ArrowUpFromLine,
  ExternalLink,
  Wallet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function History() {
  const { isSubUser } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState({ volume_bots: 0, lure_bots: 0, withdrawals: 0, zk_transactions: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (isSubUser) {
        // SUB-USER: use /api/subuser/history
        const { data } = await axios.get(`${API_URL}/api/subuser/history`, { withCredentials: true });
        setTimeline(data.timeline || []);
        setSummary({
          volume_bots: data.summary?.volume_bots || 0,
          lure_bots: data.summary?.lure_bots || 0,
          withdrawals: data.summary?.withdrawals || 0,
          zk_transactions: 0,
        });
      } else {
        // ADMIN: aggregate from existing endpoints
        const [volumeRes, lureRes, zkRes] = await Promise.all([
          axios.get(`${API_URL}/api/bot/volume/status`, { withCredentials: true }),
          axios.get(`${API_URL}/api/bot/lure/status`, { withCredentials: true }),
          axios.get(`${API_URL}/api/zk/history`, { withCredentials: true }),
        ]);
        const items = [];
        (volumeRes.data.bots || []).forEach(b => items.push({
          type: 'bot_started',
          category: 'VOLUME',
          title: `Volume bot on ${b.token_symbol || '?'}`,
          subtitle: `${b.exchange_name || ''} | ${b.trades_executed || 0} trades | $${(b.total_volume || 0).toFixed(2)} volume`,
          amount: b.profit || 0,
          status: b.status,
          bot_id: b.id || b.bot_id,
          token_symbol: b.token_symbol,
          tx_signature: b.first_tx?.tx_signature,
          explorer_url: b.first_tx?.explorer_url,
          timestamp: b.started_at,
        }));
        (lureRes.data.bots || []).forEach(b => items.push({
          type: 'bot_started',
          category: 'LURE',
          title: `Lure bot on ${b.token_symbol || '?'}`,
          subtitle: `${b.exchange_name || ''} | ${b.micro_buys_executed || 0} buys | $${(b.total_buy_volume || 0).toFixed(2)} buy vol`,
          amount: b.realized_pnl || 0,
          status: b.status,
          bot_id: b.id || b.bot_id,
          token_symbol: b.token_symbol,
          timestamp: b.started_at,
        }));
        (zkRes.data.deposits || []).forEach(d => items.push({
          type: 'zk_deposit',
          category: 'ZK',
          title: `ZK ${d.status === 'withdrawn_to_phantom' ? 'withdrawal' : 'deposit'}: ${d.amount} ${d.token}`,
          subtitle: d.recipient ? `To ${d.recipient.slice(0, 8)}...${d.recipient.slice(-4)}` : 'Anonymous',
          amount: d.amount,
          status: d.status,
          tx_signature: d.tx_signature,
          explorer_url: d.tx_signature ? `https://explorer.solana.com/tx/${d.tx_signature}?cluster=devnet` : null,
          timestamp: d.created_at,
        }));
        items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setTimeline(items);
        setSummary({
          volume_bots: (volumeRes.data.bots || []).length,
          lure_bots: (lureRes.data.bots || []).length,
          withdrawals: 0,
          zk_transactions: (zkRes.data.deposits || []).length,
        });
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
    setLoading(false);
  }, [isSubUser]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getActivityIcon = (item) => {
    if (item.type === 'withdrawal') return <ArrowUpFromLine className="w-4 h-4 text-[#10B981]" />;
    if (item.category === 'VOLUME') return <Bot className="w-4 h-4 text-[#00CED1]" />;
    if (item.category === 'LURE') return <Target className="w-4 h-4 text-[#E8520F]" />;
    if (item.category === 'ZK') return <Shield className="w-4 h-4 text-[#10B981]" />;
    if (item.type === 'zk_deposit') return <Shield className="w-4 h-4 text-[#10B981]" />;
    return <HistoryIcon className="w-4 h-4 text-[hsl(215,16%,56%)]" />;
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'running') return 'bg-green-500/15 text-green-400';
    if (s === 'stopped') return 'bg-[hsl(220,16%,18%)] text-[hsl(215,16%,50%)]';
    if (s === 'panic_stopped' || s === 'killed') return 'bg-red-500/15 text-red-400';
    if (s === 'completed' || s === 'approved' || s === 'deposited') return 'bg-green-500/15 text-green-400';
    if (s === 'pending') return 'bg-amber-500/15 text-amber-400';
    if (s === 'rejected') return 'bg-red-500/15 text-red-400';
    if (s === 'withdrawn' || s === 'withdrawn_to_phantom') return 'bg-blue-500/15 text-blue-400';
    return 'bg-[hsl(220,16%,18%)] text-[hsl(215,16%,50%)]';
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <HistoryIcon className="w-7 h-7 text-[hsl(215,16%,56%)]" />
            Activity History
          </h1>
          <p className="text-[hsl(215,16%,56%)]">
            {isSubUser
              ? 'Your bot launches, trades, and withdrawals — with on-chain transaction links'
              : 'View all bot activities and ZK vault transactions'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          className="border-[hsl(220,16%,18%)] text-white bg-transparent hover:bg-[hsl(220,16%,16%)]"
          data-testid="refresh-history-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats — sub-user shows Withdrawals; admin shows ZK Transactions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="solar-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#00CED1]/10 flex items-center justify-center">
            <Bot className="w-6 h-6 text-[#00CED1]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Volume Bots</p>
            <p className="text-2xl font-bold text-white">{summary.volume_bots}</p>
          </div>
        </div>

        <div className="solar-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#E8520F]/10 flex items-center justify-center">
            <Target className="w-6 h-6 text-[#E8520F]" />
          </div>
          <div>
            <p className="text-sm text-[hsl(215,16%,56%)]">Lure Bots</p>
            <p className="text-2xl font-bold text-white">{summary.lure_bots}</p>
          </div>
        </div>

        {isSubUser ? (
          <div className="solar-card p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <p className="text-sm text-[hsl(215,16%,56%)]">Withdrawals</p>
              <p className="text-2xl font-bold text-white">{summary.withdrawals}</p>
            </div>
          </div>
        ) : (
          <div className="solar-card p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <p className="text-sm text-[hsl(215,16%,56%)]">ZK Transactions</p>
              <p className="text-2xl font-bold text-white">{summary.zk_transactions}</p>
            </div>
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardHeader>
          <CardTitle className="text-white font-['Outfit']">Activity Timeline</CardTitle>
          <CardDescription>All activities sorted by time</CardDescription>
        </CardHeader>
        <CardContent>
          {timeline.length > 0 ? (
            <div className="space-y-3">
              {timeline.map((activity, idx) => (
                <div
                  key={`${activity.type}-${activity.bot_id || idx}-${idx}`}
                  className="flex items-start gap-4 p-4 border border-[hsl(220,16%,18%)] rounded-lg hover:bg-[hsl(220,16%,14%)] transition-colors"
                  data-testid={`activity-${activity.type}-${idx}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[hsl(220,16%,14%)] flex items-center justify-center flex-shrink-0">
                    {getActivityIcon(activity)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-white">{activity.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(activity.status)}`}>
                        {activity.status}
                      </span>
                      {activity.execution_mode && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          activity.execution_mode === 'live' ? 'bg-[#E8520F]/10 text-[#E8520F]' : 'bg-[#D4A017]/10 text-[#D4A017]'
                        }`}>
                          {activity.execution_mode === 'paper' ? 'Paper' : 'Live'}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-[hsl(215,16%,56%)]">{activity.subtitle}</p>

                    {/* PnL/Amount */}
                    {activity.amount !== undefined && activity.amount !== 0 && (
                      <p className={`text-sm font-mono font-bold mt-1 ${
                        activity.amount >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'
                      }`}>
                        {activity.amount >= 0 ? '+' : ''}${Math.abs(activity.amount).toFixed(2)}
                      </p>
                    )}

                    {/* On-chain link */}
                    {activity.explorer_url && activity.tx_signature && (
                      <a
                        href={activity.explorer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#E8520F] hover:underline mt-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View on Solscan: {activity.tx_signature.slice(0, 12)}...
                      </a>
                    )}

                    <p className="text-xs text-[hsl(215,16%,45%)] mt-1">
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[hsl(215,16%,56%)]">
              <HistoryIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No activity yet</p>
              <p className="text-sm">
                {isSubUser
                  ? 'Start a bot or make a withdrawal to see history'
                  : 'Start some bots or make a ZK deposit to see history'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
